/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  HStack,
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { FiPlus, FiUsers, FiEdit2 } from "react-icons/fi";
import api from "utils/api";
import EkHesapEkleModal from "./EkHesapEkleModal";
import EkHesapDuzenleModal from "./EkHesapDuzenleModal";

function HesapIslemleriModal({ isOpen, onClose, firma, refreshList }) {
  const firmaId = firma?.HspId;
  const firmaAdi = firma?.firma || firma?.firma_ismi || "";
  const [ekHesaplar, setEkHesaplar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editEkId, setEditEkId] = useState(null);

  const loadEkHesaplar = useCallback(async () => {
    if (!firmaId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/firmalar/ek-hesaplar/${firmaId}`);
      const result = await response.json();
      setEkHesaplar(Array.isArray(result) ? result : []);
    } catch {
      setEkHesaplar([]);
    } finally {
      setLoading(false);
    }
  }, [firmaId]);

  useEffect(() => {
    if (isOpen && firmaId) {
      loadEkHesaplar();
      setCreateOpen(false);
      setEditEkId(null);
    }
  }, [isOpen, firmaId, loadEkHesaplar]);

  const handleClose = () => {
    setCreateOpen(false);
    setEditEkId(null);
    onClose?.();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="md"
        isCentered
        motionPreset="slideInBottom"
        closeOnOverlayClick={!createOpen && !editEkId}
      >
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" boxShadow="xl" m={4} bg="white">
          <Box p={4} borderBottom="1px solid" borderColor="gray.200">
            <ModalCloseButton size="sm" />
            <HStack spacing={2}>
              <Icon as={FiUsers} color="blue.600" />
              <VStack align="start" spacing={0}>
                <Text fontSize="md" fontWeight="semibold" color="gray.800">
                  Hesap işlemleri
                </Text>
                <Text fontSize="xs" color="gray.600">
                  {firmaAdi || "Firma"} — Alt hesaplar
                </Text>
              </VStack>
            </HStack>
          </Box>

          <ModalBody px={4} py={3} maxH="320px" overflowY="auto">
            {loading ? (
              <Text fontSize="sm" color="gray.500">
                Yükleniyor...
              </Text>
            ) : ekHesaplar.length > 0 ? (
              <VStack align="stretch" spacing={2}>
                {ekHesaplar.map((ek, index) => {
                  const goEdit = () => {
                    if (ek.id != null) setEditEkId(ek.id);
                  };
                  const aracN = ek.arac_sayisi ?? 0;
                  const canSub =
                    ek.can_create_sub_users === true ||
                    (ek.userLimit != null && ek.userLimit !== -1);
                  return (
                    <Flex
                      key={ek.id ?? `${ek.ek_hesap_adi}-${index}`}
                      align="center"
                      justify="space-between"
                      gap={3}
                      px={3}
                      py={2.5}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      bg="white"
                      cursor="pointer"
                      _hover={{ borderColor: "blue.300", shadow: "sm" }}
                      onClick={goEdit}
                    >
                      <HStack spacing={2} flex={1} minW={0} align="center">
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color="gray.800"
                          letterSpacing="-0.01em"
                          noOfLines={1}
                          flex={1}
                        >
                          {ek.ek_hesap_adi}
                        </Text>
                        <IconButton
                          icon={<FiEdit2 />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="Düzenle"
                          onClick={(e) => {
                            e.stopPropagation();
                            goEdit();
                          }}
                        />
                      </HStack>
                      <HStack spacing={4} align="center" flexShrink={0}>
                        <Tooltip
                          label="Görüntülenen araç"
                          placement="top"
                          hasArrow
                          openDelay={300}
                        >
                          <Text
                            as="span"
                            fontSize="md"
                            fontWeight="700"
                            color="blue.600"
                            fontVariantNumeric="tabular-nums"
                            lineHeight="1"
                            cursor="help"
                          >
                            {aracN}
                          </Text>
                        </Tooltip>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          whiteSpace="nowrap"
                        >
                          Oluşturabilir:{" "}
                          <Text as="span" fontWeight="600" color="gray.700">
                            {canSub ? "Evet" : "Hayır"}
                          </Text>
                        </Text>
                      </HStack>
                    </Flex>
                  );
                })}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.400">
                Henüz ek hesap yok
              </Text>
            )}
          </ModalBody>

          <Divider />
          <ModalFooter py={3} px={4}>
            <Button
              size="sm"
              leftIcon={<FiPlus />}
              colorScheme="green"
              variant="solid"
              onClick={() => setCreateOpen(true)}
              isDisabled={!firmaId}
            >
              Ek hesap ekle
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <EkHesapEkleModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        firmaId={firmaId}
        firmaAdi={firmaAdi}
        refreshList={() => {
          loadEkHesaplar();
          refreshList?.();
        }}
      />

      <EkHesapDuzenleModal
        isOpen={editEkId != null}
        onClose={() => setEditEkId(null)}
        ekHesapId={editEkId}
        firmaId={firmaId}
        firmaAdi={firmaAdi}
        onSaved={() => {
          loadEkHesaplar();
          refreshList?.();
        }}
        onDeleted={() => {
          setEditEkId(null);
          loadEkHesaplar();
          refreshList?.();
        }}
      />
    </>
  );
}

export default HesapIslemleriModal;
